/* eslint-disable camelcase */

import axios from 'axios';
import { zip } from 'lodash';
import { IS_SUBMITTING_CHECKOUT, IS_SUBMITTING_PROMOCODE, SET_CHECKOUT_COMPLETE } from 'reducers/actions-types';
import {
    INVALID_PAYMENT_METHOD,
    MISSING_ADDRESS_ID,
    MISSING_DELIVERY_DATE,
    TIME_SLOT_EXPIRED,
} from 'constants/checkout-errors';
import notificationsActions from '../notifications/actions';
import { block } from 'reducers/modules/cart/helpers';
import { checkout, promoCode } from 'constants/api-urls';
import { dispatchError } from 'reducers/helpers';
import modalActions from '../modal/actions';
import cartActions from '../cart/actions';
import { t } from 'translations';
import moment from 'moment';

/**
 * Set promocode on order
 * @param {string} code
 * @return {function(*=): Promise<AxiosResponse<any> | never>}
 */
export function addPromoCode(code) {
    return function(dispatch) {
        dispatch({ type: IS_SUBMITTING_PROMOCODE, isSubmittingPromo: true });
        return axios
            .post(promoCode(), { code })
            .then(() => {
                dispatch({ type: IS_SUBMITTING_PROMOCODE, isSubmittingPromo: false });
            })
            .catch((e) => {
                if (e.status && ![400].includes(e.status)) {
                    dispatchError(dispatch, e, {
                        url: promoCode(),
                        description: e,
                    });
                }
                dispatch({ type: IS_SUBMITTING_PROMOCODE, isSubmittingPromo: false });
                throw e;
            });
    };
}

/**
 * Submit order
 * @param {string} stripeToken
 * @return {function(*=, *): Promise<AxiosResponse<any> | never>}
 */
export function submit(stripeToken) {
    return function(dispatch, getState) {
        dispatch({ type: IS_SUBMITTING_CHECKOUT, isSubmitting: true });

        return axios
            .post(checkout(), {
                ...getState().checkout,
                stripe_credit_card_token: stripeToken,
            })
            .then(({ data }) => {
                dispatch({ type: SET_CHECKOUT_COMPLETE, isComplete: true });
                dispatch(cartActions.reset());
                dispatch(notificationsActions.fetch());
                dispatch(block({
                    state: data.state,
                    id: data.id,
                }));
                dispatch({ type: IS_SUBMITTING_CHECKOUT, isSubmitting: false });
            })
            .catch(e => {
                if (e.data) {
                    dispatch(modalActions.open({
                        meta: {
                            title: t('error.occurred'),
                            description: t(e.data.error.trans_key),
                            type: 'error',
                            actions: [
                                {
                                    name: t('error.confirm'),
                                    event() {
                                        dispatch(modalActions.close());
                                    },
                                },
                            ],
                        },
                        layout: 'confirm',
                    }));
                } else {
                    dispatchError(dispatch, e, {
                        url: checkout(),
                        description: e,
                    });
                }
                dispatch({ type: IS_SUBMITTING_CHECKOUT, isSubmitting: false });
            });
    };
}

/** @type {ICartValidationError[]} */
export const defaultErrors = [
    MISSING_ADDRESS_ID,
    MISSING_DELIVERY_DATE,
    INVALID_PAYMENT_METHOD,
    TIME_SLOT_EXPIRED,
];

/**
 * True if the time slot selected by the user is in the past and or expired according to our "MINUTES_BEFORE_SLOT_EXPIRATION" constant
 * @param {moment.Moment} now - moment instance representing the current time
 * @param {string} deliveryStartDate - the delivery end date selected by the user via time picker
 * @return {boolean} true if the time slot is expired
 */
export function isTimeSlotExpired(now, deliveryStartDate) {
    // notice that here we remove the hours offset because we want always to check at beginning of the last time slot selected
    // for example if the last user slot selected is between 16:00 and 17:00 it will be considered expired when `now` is 18:00
    const end = moment(deliveryStartDate);

    if (now.hour() === end.hour()) {
        return (now.isAfter(end.add(1, 'minute')));
    }

    return now
        .add(1, 'second')
        .isAfter(end);
}

/**
 * Validate cart values
 * @param {object} state - the current cart state
 * @param {moment.Moment} currentTime
 * @returns {ICartValidationResult} if we can submit the checkout data
 */
export function validate(state, currentTime = moment()) {
    const { delivery_start_date, delivery_end_date, address_id, isPaymentMethodValid } = state;
    const tests = [
        address_id,
        delivery_start_date && delivery_end_date,
        isPaymentMethodValid,
        !delivery_end_date || !isTimeSlotExpired(currentTime, delivery_start_date),
    ];

    return {
        isValid: tests.every(te => te),
        errors: zip(tests, defaultErrors)
            .filter(([isValid]) => !isValid)
            .map(([, error]) => error),
    };
}



// WEBPACK FOOTER //
// ./mcart/assets/src/js/app/reducers/modules/checkout/helpers.js